import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DateRangePicker from "@/components/common/calendar/DateRangePicker";
import type { DateRangeWithLabel } from "@/utils/dateUtils";
import { TZDate } from "@date-fns/tz";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { formatDateRange, formatForAPI } from "@/utils/dateUtils";
import { RiArrowDownSLine, RiCalendarLine } from "@remixicon/react";
import { CreativeReportMetadata, TimeInterval } from "@/types";
import { getDefaultSevenDay } from "@/utils/dateUtils";

type Props = {
  selectedFilters: CreativeReportMetadata | null;
  setSelectedFilters: (filters: CreativeReportMetadata | null) => void;
  disabled?: boolean;
  first_allowed_date?: TZDate;
  last_allowed_date?: TZDate;
  timezone?: string;
  enable_compare?: boolean;
  onApplyToAllSections?: (filters: CreativeReportMetadata | null) => void;
  onNewDateAvailable?: () => void;
};

const CreativeReportDateSelector = ({
  selectedFilters,
  setSelectedFilters,
  disabled,
  first_allowed_date,
  last_allowed_date,
  timezone,
  enable_compare,
  onApplyToAllSections,
  onNewDateAvailable,
}: Props) => {
  const [isIntervalDropdownOpen, setIsIntervalDropdownOpen] = useState<boolean>(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<DateRangeWithLabel | null>(
    () => {
      const allowedDateRange =
        first_allowed_date && last_allowed_date
          ? {
            first_allowed_date,
            last_allowed_date,
          }
          : null;

      return getDefaultSevenDay(allowedDateRange, timezone ?? "UTC");
    },
  );

  const [tempCompareDate, setTempCompareDate] = useState<DateRangeWithLabel | null>(null);

  // At mount: schedule a single timeout for midnight in the account timezone, then show refresh notification
  useEffect(() => {
    const tz = timezone ?? "UTC";
    const nowUtc = new Date();
    const currentTimeInAccountTz = toZonedTime(nowUtc, tz);
    const year = currentTimeInAccountTz.getFullYear();
    const month = currentTimeInAccountTz.getMonth();
    const day = currentTimeInAccountTz.getDate();
    // Use JS Date UTC constructor for calendar overflow (e.g. Feb 28 → Mar 1); then
    // fromZonedTime resolves the exact UTC instant for midnight in the account TZ (DST-correct).
    const tomorrowUtcProxy = new Date(Date.UTC(year, month, day + 1));
    const tomorrowMidnightInAccountTzStr = `${tomorrowUtcProxy.getUTCFullYear()}-${String(tomorrowUtcProxy.getUTCMonth() + 1).padStart(2, "0")}-${String(tomorrowUtcProxy.getUTCDate()).padStart(2, "0")}T00:00:00`;
    const nextMidnightUtc = fromZonedTime(tomorrowMidnightInAccountTzStr, tz);
    let msUntilMidnight = nextMidnightUtc.getTime() - nowUtc.getTime();
    if (msUntilMidnight <= 0) {
      msUntilMidnight = 0;
    }

    const triggerNewDateNotification = () => {
      onNewDateAvailable?.();
    };

    const timeoutId = setTimeout(triggerNewDateNotification, msUntilMidnight);
    return () => clearTimeout(timeoutId);
  }, [timezone, onNewDateAvailable]);

  // Set initial filters if none exist
  useEffect(() => {
    if (!selectedFilters?.date && tempSelectedDate) {
      setSelectedFilters({
        ...selectedFilters,
        date: {
          start_date: formatForAPI(tempSelectedDate.from, timezone ?? "UTC"),
          end_date: formatForAPI(tempSelectedDate.to, timezone ?? "UTC"),
          label: tempSelectedDate.label,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync tempSelectedDate and tempCompareDate when dropdown closes
  useEffect(() => {
    if (!isIntervalDropdownOpen && selectedFilters?.date) {
      try {
        const startDate = new TZDate(
          fromZonedTime(selectedFilters.date.start_date, timezone ?? "UTC"),
          timezone ?? "UTC",
        );
        const endDate = new TZDate(
          fromZonedTime(selectedFilters.date.end_date, timezone ?? "UTC"),
          timezone ?? "UTC",
        );

        // Set to start of day and end of day in target timezone
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        // Preserve the stored label; only CUSTOM when user explicitly chose custom range
        const label = selectedFilters.date.label ?? TimeInterval.CUSTOM;
        setTempSelectedDate({
          from: startDate,
          to: endDate,
          label: label,
        });

        // Handle compare dates if they exist
        if (
          selectedFilters?.compare_date?.start_date &&
          selectedFilters?.compare_date?.end_date
        ) {
          const compareStartDate = new TZDate(
            fromZonedTime(selectedFilters?.compare_date?.start_date, timezone ?? "UTC"),
            timezone ?? "UTC",
          );
          const compareEndDate = new TZDate(
            fromZonedTime(selectedFilters?.compare_date?.end_date, timezone ?? "UTC"),
            timezone ?? "UTC",
          );

          compareStartDate.setHours(0, 0, 0, 0);
          compareEndDate.setHours(23, 59, 59, 999);

          // Preserve the stored label; only CUSTOM when user explicitly chose custom range
          const finalCompareLabel =
            selectedFilters?.compare_date?.label ?? TimeInterval.CUSTOM;

          setTempCompareDate({
            from: compareStartDate,
            to: compareEndDate,
            label: finalCompareLabel,
          });
        } else {
          setTempCompareDate(null);
        }
      } catch (error) {
        console.error("Error parsing dates:", error);
        setTempSelectedDate(null);
        setTempCompareDate(null);
      }
    }
  }, [
    isIntervalDropdownOpen,
    selectedFilters?.compare_date,
    selectedFilters?.date,
    timezone,
  ]);
  return (
    <DropdownMenu
      open={isIntervalDropdownOpen}
      onOpenChange={(val) => setIsIntervalDropdownOpen(val)}
    >
      <DropdownMenuTrigger
        disabled={disabled}
        className="flex h-8 items-center justify-between whitespace-nowrap rounded-[8px] bg-white hover:bg-hover-gray px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-0 squircle squircle-smooth-[1] squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        <div className="mr-2 w-full flex items-center gap-2">
          <RiCalendarLine className="h-[14px] w-[14px] text-[#9AA3BD] flex-shrink-0" />
          <span
            className={`text-brand-primary-800 text-[12px] font-medium translate-y-[1px]`}
          >
            Date Range
          </span>
          <span
            className={`${tempSelectedDate?.label === TimeInterval.CUSTOM ? "hidden" : ""
              } flex-1 text-brand-primary-600 text-[12px] font-normal translate-y-[1px]`}
          >
            <span>{tempSelectedDate ? tempSelectedDate.label : "Select date range"}</span>
            {tempCompareDate && (
              <>
                <span className="mx-1 text-[#02185966] text-[11px] font-normal">vs</span>
                <span className="font-normal">Previous</span>
              </>
            )}
          </span>
          <span
            className={`flex-1 shrink-0 overflow-hidden text-brand-primary-600 font-normal ${tempSelectedDate?.label !== TimeInterval.CUSTOM ? "hidden" : ""
              } text-ellipsis text-[12px] translate-y-[1px]`}
          >
            {tempSelectedDate && tempCompareDate ? (
              <span>
                <span>
                  {formatDateRange({
                    start_date: tempSelectedDate.from,
                    end_date: tempSelectedDate.to,
                    timezone: timezone ?? "UTC",
                  })}
                </span>
                <span className="mx-1 text-[#02185966] text-[11px] font-normal">vs</span>
                <span className="font-normal">
                  {formatDateRange({
                    start_date: tempCompareDate.from,
                    end_date: tempCompareDate.to,
                    timezone: timezone ?? "UTC",
                  })}
                </span>
              </span>
            ) : tempSelectedDate ? (
              <span>
                {formatDateRange({
                  start_date: tempSelectedDate.from,
                  end_date: tempSelectedDate.to,
                  timezone: timezone ?? "UTC",
                })}
              </span>
            ) : (
              "Select Date range"
            )}
          </span>
        </div>
        {!disabled && (
          <RiArrowDownSLine className="h-[14px] w-[14px] text-[#9AA3BD] flex-shrink-0" />
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="p-0 m-0 border-none rounded-[12px]">
        <DateRangePicker
          selected={tempSelectedDate}
          compareDate={tempCompareDate || undefined}
          hideCancelButton={true}
          onSelect={(range) => {
            if (range) {
              // Create a deep copy of the range to prevent any unintended modifications
              const newRange = {
                from: new TZDate(range.from, timezone ?? "UTC"),
                to: new TZDate(range.to, timezone ?? "UTC"),
                label: range.label || TimeInterval.CUSTOM,
              };
              setTempSelectedDate(newRange);
            }
          }}
          onCompareDateChange={(range) => {
            if (range) {
              // Create a deep copy of the range to prevent any unintended modifications
              const newRange = {
                from: new TZDate(range.from, timezone ?? "UTC"),
                to: new TZDate(range.to, timezone ?? "UTC"),
                label: range.label || TimeInterval.CUSTOM,
              };
              setTempCompareDate(newRange);
            } else {
              setTempCompareDate(null);
            }
          }}
          dateAggregate=""
          onApply={(range, _aggregate, _chunks, compareRange) => {
            if (range) {
              const newFilters: CreativeReportMetadata = {
                ...selectedFilters,
                date: {
                  start_date: formatForAPI(range.from, timezone ?? "UTC"),
                  end_date: formatForAPI(range.to, timezone ?? "UTC"),
                  label: range.label,
                },
              };

              if (compareRange?.from && compareRange?.to) {
                newFilters.compare_date = {
                  start_date: formatForAPI(compareRange.from, timezone ?? "UTC"),
                  end_date: formatForAPI(compareRange.to, timezone ?? "UTC"),
                  label: compareRange.label,
                };
              } else if ("compare_date" in newFilters) {
                delete newFilters.compare_date;
              }

              setSelectedFilters(newFilters);
              setIsIntervalDropdownOpen(false);
            }
          }}
          onApplyToAllSections={
            onApplyToAllSections
              ? (range, compareRange) => {
                if (range) {
                  const newFilters: CreativeReportMetadata = {
                    ...selectedFilters,
                    date: {
                      start_date: formatForAPI(range.from, timezone ?? "UTC"),
                      end_date: formatForAPI(range.to, timezone ?? "UTC"),
                      label: range.label,
                    },
                  };

                  if (compareRange?.from && compareRange?.to) {
                    newFilters.compare_date = {
                      start_date: formatForAPI(compareRange.from, timezone ?? "UTC"),
                      end_date: formatForAPI(compareRange.to, timezone ?? "UTC"),
                      label: compareRange.label,
                    };
                  } else if ("compare_date" in newFilters) {
                    delete newFilters.compare_date;
                  }

                  onApplyToAllSections(newFilters);
                  setIsIntervalDropdownOpen(false);
                }
              }
              : undefined
          }
          onCancel={() => setIsIntervalDropdownOpen(false)}
          autoAllowed={false}
          showAggregate={false}
          first_allowed_date={first_allowed_date}
          last_allowed_date={last_allowed_date}
          timezone={timezone ?? "UTC"}
          enableCompare={enable_compare}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CreativeReportDateSelector;
